import React from 'react';
import { Timeline, Tag, Tooltip, Empty, Spin } from 'antd';
import { CalendarOutlined, FileTextOutlined, UnorderedListOutlined, CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

// Interface para o item da timeline
interface TimelineItem {
  _id?: string;
  id?: string | number;
  tipo: 'reuniao' | 'pauta' | 'item' | 'tarefa';
  data: Date | string;
  titulo: string;
  descricao: string;
  status?: string;
  // Campos específicos por tipo
  subject?: string;
  nome?: string;
  observacoes?: string;
  regimento?: number;
  title?: string;
  description?: string;
  created_at?: Date | string;
  data_agendamento?: Date | string;
}

interface CPPDTimelineEventsProps {
  timeline: TimelineItem[];
  loading?: boolean;
  error?: string | null;
  maxItems?: number;
  showFilters?: boolean;
}

const CPPDTimelineEvents: React.FC<CPPDTimelineEventsProps> = ({ 
  timeline, 
  loading = false,
  error = null,
  maxItems = 20,
  showFilters = false
}) => {
  
  // Configurar moment.js para português
  React.useEffect(() => {
    moment.locale('pt-br');
  }, []);

  // Função para obter ícone com base no tipo
  const getItemIcon = (tipo: string, status?: string) => {
    const baseStyle = { fontSize: 16 };
    
    switch (tipo) {
      case 'reuniao':
        return status === 'Realizada' || status === 'Concluído' 
          ? <CheckCircleOutlined style={{ ...baseStyle, color: '#52c41a' }} />
          : <CalendarOutlined style={{ ...baseStyle, color: '#2E90FA' }} />;
      case 'pauta':
        return <FileTextOutlined style={{ ...baseStyle, color: '#7F56D9' }} />;
      case 'item':
        return <UnorderedListOutlined style={{ ...baseStyle, color: '#F63D68' }} />;
      case 'tarefa':
        return <CheckCircleOutlined style={{ ...baseStyle, color: '#039855' }} />;
      default:
        return <UnorderedListOutlined style={{ ...baseStyle, color: '#8c8c8c' }} />;
    }
  };

  // Função para obter cor da timeline baseada no tipo
  const getTimelineColor = (tipo: string, status?: string) => {
    if (status === 'Realizada' || status === 'Concluído') {
      return '#52c41a'; // Verde para concluído
    }
    
    switch (tipo) {
      case 'reuniao':
        return '#2E90FA'; // Azul
      case 'pauta':
        return '#7F56D9'; // Roxo
      case 'item':
        return '#F63D68'; // Rosa
      case 'tarefa':
        return '#039855'; // Verde
      default:
        return '#8c8c8c'; // Cinza
    }
  };

  // Função para mapear tipo para texto legível
  const getTipoLabel = (tipo: string) => {
    const labels: Record<string, string> = {
      'reuniao': 'Reunião',
      'pauta': 'Pauta',
      'item': 'Item de Pauta',
      'tarefa': 'Tarefa'
    };
    return labels[tipo] || tipo;
  };

  // Função para mapear regimento para label
  const getRegimentoLabel = (regimento?: number) => {
    if (!regimento) return '';
    
    const regimentoMap: Record<number, string> = {
      15: 'Quinzenal',
      30: 'Mensal',
      60: 'Bimestral',
      90: 'Trimestral'
    };
    
    return regimentoMap[regimento] || `${regimento} dias`;
  };

  // Função para processar e normalizar os dados da timeline
  const processTimelineData = (data: TimelineItem[]): TimelineItem[] => {
    if (!data || !Array.isArray(data)) return [];

    return data
      .map((item, index) => {
        // Normalizar dados do item
        const processedItem: TimelineItem = {
          _id: item._id || item.id?.toString() || `item-${index}`,
          tipo: item.tipo || 'item',
          data: item.data || item.created_at || item.data_agendamento || new Date(),
          titulo: item.titulo || item.nome || item.subject || item.title || 'Evento sem título',
          descricao: item.descricao || item.observacoes || item.description || 'Sem descrição',
          status: item.status
        };

        // Ajustes específicos por tipo
        switch (processedItem.tipo) {
          case 'reuniao':
            if (!processedItem.titulo.includes('Reunião')) {
              processedItem.titulo = `Reunião: ${processedItem.titulo}`;
            }
            if (item.data_agendamento && !processedItem.descricao.includes('Agendada')) {
              processedItem.descricao = `Agendada para ${moment(item.data_agendamento).format('DD/MM/YYYY HH:mm')}`;
            }
            break;
            
          case 'pauta':
            if (item.regimento) {
              const regimentoLabel = getRegimentoLabel(item.regimento);
              processedItem.titulo = `Pauta ${regimentoLabel}`;
              if (!processedItem.descricao.includes('Regimento')) {
                processedItem.descricao = `Regimento: ${regimentoLabel}. ${processedItem.descricao}`;
              }
            }
            break;
            
          case 'item':
            if (!processedItem.titulo.includes('Item')) {
              processedItem.titulo = `Item: ${processedItem.titulo}`;
            }
            break;
        }

        return processedItem;
      })
      // Ordenar por data (mais recente primeiro)
      .sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime())
      // Limitar quantidade de itens
      .slice(0, maxItems);
  };

  // Estados de carregamento e erro
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Spin tip="Carregando timeline..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Empty 
          description={error}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  // Processar dados da timeline
  const processedTimeline = processTimelineData(timeline);

  // Verificar se há dados para exibir
  if (!processedTimeline || processedTimeline.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <Empty 
          description="Nenhum evento encontrado"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </div>
    );
  }

  return (
    <div className="cppd-timeline-events">
      <Timeline mode="left">
        {processedTimeline.map((item, index) => {
          const itemDate = moment(item.data);
          const isToday = itemDate.isSame(moment(), 'day');
          const isThisWeek = itemDate.isSame(moment(), 'week');
          
          return (
            <Timeline.Item
              key={item._id || index}
              dot={getItemIcon(item.tipo, item.status)}
              color={getTimelineColor(item.tipo, item.status)}
            >
              <div className="timeline-item-content">
                {/* Cabeçalho do item */}
                <div className="timeline-item-header" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  marginBottom: 8,
                  gap: 12
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ 
                      margin: 0, 
                      color: '#262626',
                      fontSize: '14px',
                      fontWeight: 600,
                      lineHeight: '20px'
                    }}>
                      {item.titulo}
                    </h4>
                    
                    {/* Tags de tipo e status - CORRIGIDO */}
                    <div style={{ marginTop: 4, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      <Tag 
                        color={getTimelineColor(item.tipo, item.status)}
                        style={{ 
                          fontSize: '11px', 
                          lineHeight: '16px',
                          height: '20px',
                          padding: '0 6px',
                          borderRadius: '10px'
                        }}
                      >
                        {getTipoLabel(item.tipo)}
                      </Tag>
                      
                      {item.status && (
                        <Tag
                          color={
                            item.status === 'Concluído' || item.status === 'Realizada'
                              ? 'success'
                              : item.status === 'Em andamento'
                              ? 'processing'
                              : 'warning'
                          }
                          style={{ 
                            fontSize: '11px', 
                            lineHeight: '16px',
                            height: '20px',
                            padding: '0 6px',
                            borderRadius: '10px'
                          }}
                        >
                          {item.status}
                        </Tag>
                      )}
                    </div>
                  </div>
                  
                  {/* Data e tempo relativo */}
                  <div style={{ 
                    textAlign: 'right',
                    minWidth: 'fit-content'
                  }}>
                    <Tooltip title={itemDate.format('DD/MM/YYYY HH:mm')}>
                      <div style={{ 
                        fontSize: '12px', 
                        color: isToday ? '#52c41a' : isThisWeek ? '#1890ff' : '#8c8c8c',
                        fontWeight: isToday ? 600 : 400,
                        lineHeight: '16px'
                      }}>
                        {itemDate.fromNow()}
                      </div>
                      <div style={{ 
                        fontSize: '11px', 
                        color: '#bfbfbf',
                        lineHeight: '14px'
                      }}>
                        {itemDate.format('DD/MM/YY')}
                      </div>
                    </Tooltip>
                  </div>
                </div>
                
                {/* Conteúdo da descrição */}
                <div className="timeline-item-description">
                  <p style={{ 
                    margin: 0, 
                    color: '#595959',
                    fontSize: '13px',
                    lineHeight: '18px'
                  }}>
                    {item.descricao}
                  </p>
                </div>
              </div>
            </Timeline.Item>
          );
        })}
      </Timeline>
      
      {/* Rodapé informativo */}
      {processedTimeline.length >= maxItems && (
        <div style={{ 
          textAlign: 'center', 
          padding: '12px',
          borderTop: '1px solid #f0f0f0',
          marginTop: '16px'
        }}>
          <span style={{ 
            fontSize: '12px', 
            color: '#8c8c8c',
            fontStyle: 'italic'
          }}>
            Exibindo os {maxItems} eventos mais recentes
          </span>
        </div>
      )}
    </div>
  );
};

export default CPPDTimelineEvents;